/*
 * themeUtils.ts
 * Resolves and applies the interface theme of the user. The user
 * chooses a mode, auto follows the system setting and switches
 * from the dark theme to the night theme late in the evening.
 */

// The mode chosen by the user and the theme actually shown
export type ThemeMode = 'auto' | 'light' | 'dark' | 'night';
export type ResolvedTheme = 'light' | 'dark' | 'night';

// All modes the user can choose from
export const THEME_MODES: ThemeMode[] = ['auto', 'light', 'dark', 'night'];

// The local storage key holding the chosen mode to apply it before the preferences load
const THEME_STORAGE_KEY = 'theme_mode';

// The hours between which the auto mode shows the night theme instead of the dark theme
const NIGHT_START_HOUR = 23;
const NIGHT_END_HOUR = 6;

// Read the stored theme mode, falls back to auto
export const getStoredThemeMode = (): ThemeMode => {
    try {
        const storedMode = localStorage.getItem(THEME_STORAGE_KEY);
        return THEME_MODES.includes(storedMode as ThemeMode) ? (storedMode as ThemeMode) : 'auto';
    } catch (error) {
        return 'auto';
    }
};

// Store the theme mode so the next page load applies it right away
export const storeThemeMode = (mode: ThemeMode) => {
    try {
        localStorage.setItem(THEME_STORAGE_KEY, mode);
    } catch (error) {
        // Ignore a blocked local storage, the mode is applied anyway
    }
};

// Check whether the system of the user prefers a dark color scheme
export const systemPrefersDark = (): boolean => {
    return typeof window.matchMedia === 'function' && window.matchMedia('(prefers-color-scheme: dark)').matches;
};

// Check whether the given time falls in the night hours
export const isNightHour = (date: Date = new Date()): boolean => {
    const hour = date.getHours();
    return hour >= NIGHT_START_HOUR || hour < NIGHT_END_HOUR;
};

// Resolve the theme to show for a mode
export const resolveTheme = (mode: ThemeMode, prefersDark: boolean = systemPrefersDark(), date: Date = new Date()): ResolvedTheme => {
    // A fixed mode is shown as is
    if (mode !== 'auto') return mode;

    // Follow the system setting, late in the evening the night theme replaces the dark theme
    if (!prefersDark) return 'light';
    return isNightHour(date) ? 'night' : 'dark';
};

// Apply a resolved theme on the document so the theme variables take effect
export const applyResolvedTheme = (theme: ResolvedTheme) => {
    document.documentElement.dataset.theme = theme;
    document.documentElement.style.colorScheme = theme === 'light' ? 'light' : 'dark';
};

// Resolve and apply a theme mode, returns the theme shown
export const applyThemeMode = (mode: ThemeMode): ResolvedTheme => {
    const resolvedTheme = resolveTheme(mode);
    applyResolvedTheme(resolvedTheme);
    return resolvedTheme;
};
